import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Block, DateRange, DefaultText, FullScreenSpinner, MediumText, UpdateIcon } from '@guryou/html-components';
import { saveCoursePlusClass, stateIsLoading, useEffectOnError, useEffectOnSuccess } from '@guryou/redux';
import { addMonths } from '@guryou/utils/build/dateTimeUtils';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import CalendarDuration from 'onboard/components/CalendarDuration';
import OnboardingSlider from 'onboard/components/OnboardingSlider';
import CheckboxInfoContainer from 'onboard/containers/CheckboxInfoContainer';
import FormContainer from 'onboard/containers/FormContainer';
import InfoContainer from 'onboard/containers/InfoContainer';
import MainContainer from 'onboard/containers/MainContainer';
import PlayGuideVideoEmbed from 'onboard/containers/PlayGuideVideoEmbed';
import TitleContainer from 'onboard/containers/TitleContainer';
import { useOnboardContext } from 'onboard/context';
import { guideVideos } from 'onboard/GuideVideosUrls';
import { dealTokens, SystemFormGrid, SystemPriceInput, SystemSelect } from 'components/shared';

import StepButtons from '../StepButtons';

const CourseFinalDetails = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { courseState, serviceType, setCreatedCourseIds, setActiveStep } = useOnboardContext();

  const [value, setValue] = useState(courseState.duration || 60);
  const [preparationTime, setPreparationTime] = useState(courseState.preparationTime || 0);

  const createCourse = useSelector(state => state.classes.createCoursePlusClass);

  const handleValueChange = val => {
    setValue(val);
    if (preparationTime > val) {
      setPreparationTime(Math.max(val, 0));
    }
  };

  const handlePreparationTime = val => {
    setPreparationTime(val);
  };

  const handleSubmit = data => {
    const { location, ...restState } = courseState;
    dispatch(saveCoursePlusClass({ ...restState, ...data, ...location }));
  };

  useEffectOnSuccess(
    state => state.classes.createCoursePlusClass,
    data => {
      //id = serviceId, id2 = classId
      setCreatedCourseIds(data?.id, data?.id2);
    }
  );

  useEffectOnError(
    state => state.classes.createCoursePlusClass,
    () => {
      setActiveStep(0);
    }
  );

  if (stateIsLoading(createCourse)) {
    return <FullScreenSpinner />;
  }

  return (
    <MainContainer>
      <FormContainer handleSubmit={handleSubmit} parserVersion={2}>
        <TitleContainer title={t(`ONBOARD.${serviceType}Details`)} />
        <Block lg={10} md={12} xs={12} margin={0} marginTop={6}>
          <SystemFormGrid singleColumn>
            <SystemPriceInput name="price" label={t('ONBOARD.price')} defaultValue={courseState.price} required />
            <SystemSelect
              name="model"
              required
              label={t('ONBOARD.model')}
              value={courseState.model}
              none={false}
              options={[
                { label: t('GENERAL.perSession'), value: 'session' },
                { label: t('PROFILE.total'), value: 'total' },
              ]}
            />
          </SystemFormGrid>
          <DateRange
            inline={true}
            xs={5.8}
            wrapperMargin={0}
            wrapperJustifyContent="space-between"
            margin="10px 0px 20px 0px"
            startDateFieldName="startTime"
            endDateFieldName="endTime"
            startValue={courseState.startTime || new Date()}
            endValue={courseState.endTime || addMonths(new Date(), 1)}
            startWithNullDate={true}
          />
          <OnboardingSlider
            name="duration"
            label={t('ONBOARD.duration')}
            defaultValue={courseState.duration}
            min={5}
            step={5}
            max={600}
            maxSlider={120}
            icon={<AccessTimeIcon />}
            margin={'20px 0px 20px 0px'}
            onChange={handleValueChange}
          />
          <OnboardingSlider
            name="preparationTime"
            label={t('ONBOARD.preparationTime')}
            min={0}
            value={preparationTime}
            defaultValue={preparationTime}
            step={5}
            max={value}
            maxSlider={value}
            icon={<UpdateIcon />}
            margin={'20px 0px 20px 0px'}
            onChange={handlePreparationTime}
          />
        </Block>
        <StepButtons />
      </FormContainer>
      <Block lg={5.6} md={5.8} xs={12} padding={0} margin={0}>
        <InfoContainer nested>
          <MediumText lineHeight="1.3">{t('ONBOARD_TEXT.courseFinalDetailsTitle')}</MediumText>
          <DefaultText color={dealTokens.color.textMuted} marginTop={2}>
            {t('ONBOARD_TEXT.courseFinalDetailsSubtitle')}
          </DefaultText>

          <div style={{ marginTop: dealTokens.space[6], paddingTop: dealTokens.space[5], borderTop: `1px solid ${dealTokens.color.border}` }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: dealTokens.font.weightSemibold,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: dealTokens.color.textMuted,
                marginBottom: dealTokens.space[4],
              }}
            >
              {t('ONBOARD_TEXT.courseFinalDetailsCheckboxTitle')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: dealTokens.space[4] }}>
              <CheckboxInfoContainer text={t('ONBOARD_TEXT.courseFinalDetailsCheckbox1Text')} />
              <CheckboxInfoContainer text={t('ONBOARD_TEXT.courseFinalDetailsCheckbox2Text')} />
            </div>
          </div>

          <PlayGuideVideoEmbed
            videoId={guideVideos[`${serviceType}FinalDetails`].videoId}
            modalTitle={guideVideos[`${serviceType}FinalDetails`].title}
          />
        </InfoContainer>
        <CalendarDuration value={value} preparationTime={preparationTime} />
      </Block>
    </MainContainer>
  );
};

export default CourseFinalDetails;
